import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  writeBatch,
  serverTimestamp,
  QueryConstraint,
} from "firebase/firestore";
import {firestore} from "./config";
import {UserProfile, UserRole} from "./auth";

// Type definitions for Firestore documents
export interface FirestoreSession {
  id?: string;
  name: string;
  date: Timestamp;
  location: string;
  duration?: string;
  titanId: string;
  customWorldName?: string; // For custom worlds when titanId is empty
  description: string;
  players: Array<{
    playerId: string;
    displayName: string;
    characterId: string;
    characterName: string;
    characterClass: string;
    characterLevel: number;
  }>;
  maxPlayers: number;
  status: "upcoming" | "available" | "completed";
  dmId: string;
  levelRange: string;
  sessionLog?: {
    events: string;
    items: Array<{
      name: string;
      type: string;
      amount: number;
      description: string;
    }>;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface FirestoreCharacter {
  id?: string;
  name: string;
  species: string;
  class: string;
  level: number;

  playerId: string;
  mementoTokens: number;
  downtimeDays: number;
  status: "active" | "inactive" | "retired";
  inventory: Array<{
    name: string;
    type: string;
    amount: number;
    description: string;
  }>;
  currency?: {
    platinum: number;
    gold: number;
    electrum: number;
    silver: number;
    copper: number;
  };
  sheetUrl?: string;
  sheetPath?: string;
  fileName?: string;
  will?: {
    beneficiary: string;
    description: string;
  };
  sessionsPlayed: number;
  lastPlayed?: Timestamp;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface FirestoreTitan {
  id?: string;
  name: string;
  description: string;
  links: string[];
  dmId: string;
  levelRange: string;
  specialties: string[];
  sessions: string[]; // Session IDs
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Base Firestore service class
class FirestoreService {
  // Generic CRUD operations
  async getCollection<T>(collectionName: string): Promise<T[]> {
    try {
      const querySnapshot = await getDocs(
        collection(firestore, collectionName)
      );
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as T[];
    } catch (error) {
      // Error handling for collection fetch operations
      throw error;
    }
  }

  private async getDocument<T>(
    collectionName: string,
    docId: string
  ): Promise<T | null> {
    try {
      const docRef = doc(firestore, collectionName, docId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as T;
      }
      return null;
    } catch (error) {
      // Error handling for document fetch operations
      throw error;
    }
  }

  async addDocument(collectionName: string, data: any): Promise<string> {
    try {
      const docRef = await addDoc(collection(firestore, collectionName), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      // Error handling for document creation operations
      throw error;
    }
  }

  private async updateDocument<T>(
    collectionName: string,
    docId: string,
    data: Partial<T>
  ): Promise<void> {
    try {
      const docRef = doc(firestore, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      // Error handling for document update operations
      throw error;
    }
  }

  async deleteDocument(collectionName: string, docId: string): Promise<void> {
    try {
      const docRef = doc(firestore, collectionName, docId);
      await deleteDoc(docRef);
    } catch (error) {
      // Error handling for document deletion operations
      throw error;
    }
  }

  private async queryCollection<T>(
    collectionName: string,
    constraints: QueryConstraint[]
  ): Promise<T[]> {
    try {
      const q = query(collection(firestore, collectionName), ...constraints);
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as T[];
    } catch (error) {
      // Error handling for collection query operations
      throw error;
    }
  }

  // User management functions
  async getUsers(): Promise<UserProfile[]> {
    try {
      const querySnapshot = await getDocs(collection(firestore, "users"));
      return querySnapshot.docs.map((doc) => ({
        uid: doc.id,
        ...doc.data(),
      })) as UserProfile[];
    } catch (error) {
      // Error handling for user fetch operations
      throw error;
    }
  }

  async getUserById(uid: string): Promise<UserProfile | null> {
    try {
      const userDoc = await getDoc(doc(firestore, "users", uid));
      if (userDoc.exists()) {
        return {
          uid: userDoc.id,
          ...userDoc.data(),
        } as UserProfile;
      }
      return null;
    } catch (error) {
      // Error handling for individual user fetch operations
      throw error;
    }
  }

  async updateUser(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const userRef = doc(firestore, "users", uid);
      await updateDoc(userRef, {
        ...updates,
        lastActive: serverTimestamp(),
      });
    } catch (error) {
      // Error handling for user update operations
      throw error;
    }
  }

  async deleteUser(uid: string): Promise<void> {
    try {
      const userRef = doc(firestore, "users", uid);
      await deleteDoc(userRef);
    } catch (error) {
      // Error handling for user deletion operations
      throw error;
    }
  }

  async getActiveUsers(): Promise<UserProfile[]> {
    try {
      // Query users collection for active users
      return this.queryCollection<UserProfile>("users", [
        where("isActive", "==", true),
      ]);
    } catch (error) {
      // Error handling for active users fetch operations
      throw error;
    }
  }

  async getUsersByRole(role: UserRole): Promise<UserProfile[]> {
    try {
      // Query users collection for users with the specified role
      return this.queryCollection<UserProfile>("users", [
        where("roles", "array-contains", role),
      ]);
    } catch (error) {
      // Error handling for role-based user fetch operations
      throw error;
    }
  }

  // Sessions functions
  async getSessions(): Promise<FirestoreSession[]> {
    return this.getCollection<FirestoreSession>("sessions");
  }

  async getSessionById(id: string): Promise<FirestoreSession | null> {
    return this.getDocument<FirestoreSession>("sessions", id);
  }

  // Note: Session creation, updates, and deletion are now handled by cloud functions
  // Use SessionService for these operations

  async getSessionsByStatus(
    status: FirestoreSession["status"]
  ): Promise<FirestoreSession[]> {
    return this.queryCollection<FirestoreSession>("sessions", [
      where("status", "==", status),
    ]);
  }

  async getSessionsByDM(dmId: string): Promise<FirestoreSession[]> {
    return this.queryCollection<FirestoreSession>("sessions", [
      where("dmId", "==", dmId),
    ]);
  }

  async getSessionsByTitan(titanId: string): Promise<FirestoreSession[]> {
    return this.queryCollection<FirestoreSession>("sessions", [
      where("titanId", "==", titanId),
    ]);
  }

  async getUpcomingSessions(): Promise<FirestoreSession[]> {
    return this.getSessionsByStatus("upcoming");
  }

  async getAvailableSessions(): Promise<FirestoreSession[]> {
    return this.getSessionsByStatus("available");
  }

  async getCompletedSessions(): Promise<FirestoreSession[]> {
    return this.getSessionsByStatus("completed");
  }

  // Characters functions
  async getCharacters(): Promise<FirestoreCharacter[]> {
    return this.getCollection<FirestoreCharacter>("characters");
  }

  async getCharacterById(id: string): Promise<FirestoreCharacter | null> {
    return this.getDocument<FirestoreCharacter>("characters", id);
  }

  async createCharacter(
    character: Omit<FirestoreCharacter, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    return this.addDocument("characters", character);
  }

  async updateCharacter(
    id: string,
    updates: Partial<FirestoreCharacter>
  ): Promise<void> {
    return this.updateDocument<FirestoreCharacter>("characters", id, updates);
  }

  async deleteCharacter(id: string): Promise<void> {
    return this.deleteDocument("characters", id);
  }

  async getCharactersByPlayer(playerId: string): Promise<FirestoreCharacter[]> {
    return this.queryCollection<FirestoreCharacter>("characters", [
      where("playerId", "==", playerId),
    ]);
  }

  async getCharactersByLevel(
    minLevel: number,
    maxLevel: number
  ): Promise<FirestoreCharacter[]> {
    return this.queryCollection<FirestoreCharacter>("characters", [
      where("level", ">=", minLevel),
      where("level", "<=", maxLevel),
    ]);
  }

  // Players functions - these should query the users collection
  async getPlayers(): Promise<UserProfile[]> {
    try {
      // Query users collection for users with player role
      return this.queryCollection<UserProfile>("users", [
        where("roles", "array-contains", "player"),
      ]);
    } catch (error) {
      // Error handling for player fetch operations
      throw error;
    }
  }

  async getPlayerById(id: string): Promise<UserProfile | null> {
    return this.getDocument<UserProfile>("users", id);
  }

  async updatePlayer(id: string, updates: Partial<UserProfile>): Promise<void> {
    return this.updateDocument<UserProfile>("users", id, updates);
  }

  async getActivePlayers(): Promise<UserProfile[]> {
    try {
      // Query users collection for active users with player role
      return this.queryCollection<UserProfile>("users", [
        where("roles", "array-contains", "player"),
        where("isActive", "==", true),
      ]);
    } catch (error) {
      // Error handling for active player fetch operations
      throw error;
    }
  }

  // DMs functions - these should query the users collection
  async getDMs(): Promise<UserProfile[]> {
    try {
      // Query users collection for users with dm role
      return this.queryCollection<UserProfile>("users", [
        where("roles", "array-contains", "dm"),
      ]);
    } catch (error) {
      // Error handling for DM fetch operations
      throw error;
    }
  }

  async getDMById(id: string): Promise<UserProfile | null> {
    return this.getDocument<UserProfile>("users", id);
  }

  async updateDM(id: string, updates: Partial<UserProfile>): Promise<void> {
    return this.updateDocument<UserProfile>("users", id, updates);
  }

  async getActiveDMs(): Promise<UserProfile[]> {
    try {
      // Query users collection for active users with dm role
      return this.queryCollection<UserProfile>("users", [
        where("roles", "array-contains", "dm"),
        where("isActive", "==", true),
      ]);
    } catch (error) {
      // Error handling for active DM fetch operations
      throw error;
    }
  }

  // Titans functions
  async getTitans(): Promise<FirestoreTitan[]> {
    return this.getCollection<FirestoreTitan>("titans");
  }

  async getTitanById(id: string): Promise<FirestoreTitan | null> {
    return this.getDocument<FirestoreTitan>("titans", id);
  }

  // Note: Titan creation, updates, and deletion are now handled by cloud functions
  // Use TitanService for these operations

  async getTitansByDM(dmId: string): Promise<FirestoreTitan[]> {
    return this.queryCollection<FirestoreTitan>("titans", [
      where("dmId", "==", dmId),
    ]);
  }

  async getActiveTitans(): Promise<FirestoreTitan[]> {
    try {
      // Get all titans and filter for active ones
      const allTitans = await this.getTitans();
      return allTitans.filter((titan) => titan.isActive === true);
    } catch (error) {
      // Error handling for active titan fetch operations
      throw error;
    }
  }

  // Real-time listeners
  onSessionsSnapshot(callback: (sessions: FirestoreSession[]) => void) {
    return onSnapshot(collection(firestore, "sessions"), (snapshot) => {
      const sessions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as FirestoreSession[];
      callback(sessions);
    });
  }

  onCharactersSnapshot(callback: (characters: FirestoreCharacter[]) => void) {
    return onSnapshot(collection(firestore, "characters"), (snapshot) => {
      const characters = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as FirestoreCharacter[];
      callback(characters);
    });
  }

  onPlayersSnapshot(callback: (players: UserProfile[]) => void) {
    return onSnapshot(collection(firestore, "users"), (snapshot) => {
      const players = snapshot.docs.map((doc) => ({
        uid: doc.id,
        ...doc.data(),
      })) as UserProfile[];
      callback(players);
    });
  }

  onUsersSnapshot(callback: (users: UserProfile[]) => void) {
    return onSnapshot(collection(firestore, "users"), (snapshot) => {
      const users = snapshot.docs.map((doc) => ({
        uid: doc.id,
        ...doc.data(),
      })) as UserProfile[];
      callback(users);
    });
  }

  onTitansSnapshot(callback: (titans: FirestoreTitan[]) => void) {
    return onSnapshot(collection(firestore, "titans"), (snapshot) => {
      const titans = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as FirestoreTitan[];
      callback(titans);
    });
  }

  onTitansByDMSnapshot(
    dmId: string,
    callback: (titans: FirestoreTitan[]) => void
  ) {
    if (!dmId) {
      callback([]);
      return () => {};
    }

    const titansQuery = query(
      collection(firestore, "titans"),
      where("dmId", "==", dmId)
    );

    return onSnapshot(titansQuery, (snapshot) => {
      const titans = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as FirestoreTitan[];
      callback(titans);
    });
  }

  // Batch operations
  async batchUpdate(
    updates: Array<{
      collection: string;
      id: string;
      data: any;
    }>
  ): Promise<void> {
    try {
      const batch = writeBatch(firestore);

      updates.forEach(({collection: collectionName, id, data}) => {
        const docRef = doc(firestore, collectionName, id);
        batch.update(docRef, {
          ...data,
          updatedAt: serverTimestamp(),
        });
      });

      await batch.commit();
    } catch (error) {
      // Error handling for batch update operations
      throw error;
    }
  }

  // Search functions
  async searchSessions(query: string): Promise<FirestoreSession[]> {
    try {
      const sessions = await this.getSessions();
      return sessions.filter(
        (session) =>
          session.name.toLowerCase().includes(query.toLowerCase()) ||
          session.description.toLowerCase().includes(query.toLowerCase()) ||
          session.location.toLowerCase().includes(query.toLowerCase())
      );
    } catch (error) {
      // Error handling for session search operations
      throw error;
    }
  }

  async searchCharacters(query: string): Promise<FirestoreCharacter[]> {
    try {
      const characters = await this.getCharacters();
      return characters.filter(
        (character) =>
          character.name.toLowerCase().includes(query.toLowerCase()) ||
          character.species.toLowerCase().includes(query.toLowerCase()) ||
          character.class.toLowerCase().includes(query.toLowerCase())
      );
    } catch (error) {
      // Error handling for character search operations
      throw error;
    }
  }

  async searchUsers(query: string): Promise<UserProfile[]> {
    try {
      const users = await this.getUsers();
      return users.filter(
        (user) =>
          user.displayName.toLowerCase().includes(query.toLowerCase()) ||
          user.email.toLowerCase().includes(query.toLowerCase()) ||
          (user.bio && user.bio.toLowerCase().includes(query.toLowerCase()))
      );
    } catch (error) {
      // Error handling for user search operations
      throw error;
    }
  }
}

const firestoreService = new FirestoreService();
export default firestoreService;
